import {
  ChangeDetectionStrategy,
  Component,
  effect,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Button } from 'primeng/button';
import { IftaLabel } from 'primeng/iftalabel';
import { InputText } from 'primeng/inputtext';
import { generateRandomString } from '@bindoc-projects/util-lib/logic';
import {
  BD_UI_SALT_INPUT_CONFIG_DEFAULT,
  BdUiSaltInputConfig,
} from '../config';

@Component({
  selector: 'bd-ui-salt-input',
  template: `
    <div class="bd-ui-salt-input">
      @if (config().title) {
        <h3 class="bd-ui-salt-input__title">{{ config().title }}</h3>
      }

      <div class="bd-ui-salt-input__controls">
        <p-iftalabel class="bd-ui-salt-input__field">
          <input
            pInputText
            inputId="bd-ui-salt-input"
            type="text"
            variant="filled"
            fluid
            [ngModel]="salt()"
            [disabled]="disabled()"
            (ngModelChange)="onSaltChange($event)"
            (blur)="onTouched()"
            aria-describedby="salt-hint-primary salt-hint-secondary"
          />
          <label for="bd-ui-salt-input">{{ config().inputLabel }}</label>
        </p-iftalabel>

        <p-button
          [label]="config().generateButtonLabel"
          severity="secondary"
          [disabled]="disabled()"
          (click)="generateRandomSalt()"
        />
      </div>

      @if (config().showHints) {
        <div class="bd-ui-salt-input__hints">
          @if (config().primaryHint) {
            <small
              id="salt-hint-primary"
              class="bd-ui-salt-input__hint bd-ui-salt-input__hint--primary"
            >
              <strong>Wichtig:</strong> {{ config().primaryHint }}
            </small>
          }
          @if (config().secondaryHint) {
            <small
              id="salt-hint-secondary"
              class="bd-ui-salt-input__hint bd-ui-salt-input__hint--secondary"
            >
              {{ config().secondaryHint }}
            </small>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .bd-ui-salt-input__controls {
        display: flex;
        gap: 1rem;
        align-items: flex-start;
      }

      /* the previous Material field supplied its own width; p-iftalabel is a
         plain inline wrapper, so the flex child needs the growth hint here */
      .bd-ui-salt-input__field {
        flex: 1 1 auto;
        min-width: 0;
      }

      .bd-ui-salt-input__hints {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-top: 0.5rem;
      }

      .bd-ui-salt-input__hint {
        display: block;
        color: var(--p-text-muted-color);
      }
    `,
  ],
  imports: [IftaLabel, InputText, FormsModule, Button],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BdUiSaltInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdUiSaltInputComponent implements ControlValueAccessor {
  readonly config = input<BdUiSaltInputConfig>(BD_UI_SALT_INPUT_CONFIG_DEFAULT);
  /**
   * Receives the initial salt from the parent binding `[salt]="…"`. The alias is
   * required, not cosmetic: the class already exposes a mutable `salt` signal
   * for the current value, so the input needs a distinct property name while
   * keeping the public template API that all consumers bind to.
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  readonly saltInput = input<string | null>(null, { alias: 'salt' });
  readonly salt = signal<string | null>(null);
  readonly disabled = signal(false);

  constructor() {
    effect(() => {
      this.salt.set(this.saltInput());
    });
  }

  readonly saltChange = output<string>();

  // ControlValueAccessor callbacks - these are intentionally no-op defaults that get replaced by registerOnChange/registerOnTouched
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  public generateRandomSalt(): void {
    const newSalt = generateRandomString({
      length: this.config().generatedSaltLength,
      lowercase: true,
      uppercase: true,
      numbers: true,
      symbols: true,
    });
    this.salt.set(newSalt);
    this._emitChange(newSalt);
  }

  public onSaltChange(newSalt: string): void {
    this.salt.set(newSalt);
    this._emitChange(newSalt);
  }

  writeValue(value: string): void {
    this.salt.set(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private _emitChange(value: string): void {
    this.saltChange.emit(value);
    this._onChange(value);
  }
}
